<template>
  <div
    v-if="!readOnly"
    class="ScreenActions-top u-floatRight"
  >
    <button
      class="Button Button-transparent u-floatRight RemoveScreenButton"
      @click="handleRemoveScreen"
    >
      <span class="Icon-s Icon-trash" />
    </button>

    <delete-dynamic-screen-modal
      v-model="ui.showDeleteScreenModal"
      :screen="screen"
    />
    <div
      v-if="question"
      class="LayoutSelection-container u-floatRight"
    >
      <template v-if="showReplaceQuestion">
        <button
          class="Button Button-transparent ReplaceQuestionButton"
          @click="handleReplaceQuestion"
        >
          <span
            class="Icon Icon-exchange-alt"
          />
        </button>
      </template>
      <button
        class="Button Button-transparent SelectLayoutButton"
        @click.stop="handleToggleScreenConfigurationMenu('layout', !ui.showLayoutMenu)"
      >
        <span class="Icon-s Icon-paint-brush" />  Layout
      </button>
      <template v-if="ui.showLayoutMenu">
        <screen-layout-menu
          v-click-outside="handleCloseScreenLayoutMenu"
          :question-layout-type="screen.layout.type"
          @screen-layout-menu:set-layout-type="handleSetScreenLayoutType"
        />
      </template>
      <template v-if="shouldShowMediaMenu">
        <button
          class="Button Button-transparent SelectMediaConfigurationButton"
          @click.stop="handleToggleScreenConfigurationMenu('media', !ui.showMediaMenu)"
        >
          <span class="Icon-s Icon-ellipsis-v" />
        </button>
      </template>

      <media-menu
        v-show="ui.showMediaMenu"
        v-click-outside="handleCloseMediaMenu"
        :question="question"
        :type="mediaType"
        @media-menu:change-media="handleChangeMedia"
        @media-menu:remove-media="handleRemoveMedia"
        @media-menu:set-alignment="handleSetAlignment"
      />
    </div>
    <add-layout-media-modal
      v-model=" ui.showAddLayoutMediaModal"
      :question="question"
      :layout-type="screen.layout.type"
    />
    <no-video-layout-support-modal
      v-model="ui.showNoVideoLayoutModal"
      :question="question"
      :layout-type="newLayoutType"
    />
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import ScreenLayoutMenu from './screen-layout-menu';
import MediaMenu from './media-menu';
import AddLayoutMediaModal from '../../../modals/add-layout-media';
import NoVideoLayoutSupportModal from '../../../modals/no-video-layout-support';
import DeleteDynamicScreenModal from '../../../modals/delete-dynamic-screen';

export default {
  name: 'ScreenActionsMenu',

  components: {
    DeleteDynamicScreenModal,
    ScreenLayoutMenu,
    NoVideoLayoutSupportModal,
    MediaMenu,
    AddLayoutMediaModal,
  },

  props: {
    screen: {
      type: Object,
      default() {
        return {};
      },
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      mediaType: 'image',
      newLayoutType: null,
      ui: {
        showLayoutMenu: false,
        showMediaMenu: false,
        showDeleteScreenModal: false,
        showAddLayoutMediaModal: false,
        showNoVideoLayoutModal: false,
      },
    };
  },
  computed: {
    ...mapState('forms', {
      questions: (state) => state.form.questions,
      selectedQuestion: (state) => state.selectedQuestion,
    }),
    ...mapGetters('forms', ['dynamicFormQuestionList']),
    question() {
      return this.screen.questionVuexId ? this.dynamicFormQuestionList[this.screen.questionVuexId] : null;
    },
    showReplaceQuestion() {
      return this.question.can_be_replaced;
    },
    shouldShowMediaMenu() {
      if (this.question.type === 'statement') return true;
      return this.screen.layout && this.screen.layout.type !== 'text-only';
    },
  },
  methods: {
    ...mapMutations('forms', [
      'updateSelectedQuestion',
      'setFormQuestions',
      'setScreenLayoutType',
      'addQuestionToScreen',
      'resetSelectedQuestion',
    ]),
    ...mapActions('forms', ['removeFormQuestion', 'removeDynamicFormScreen']),
    handleSetScreenLayoutType(layoutType) {
      if (this.question.layout !== undefined && this.question.layout !== null) {
        const { image, videoId } = this.question.layout;
        this.newLayoutType = null;
        this.ui.showLayoutMenu = false;

        // Show media modal if the question has no media set
        if (layoutType !== 'media-middle' && !!videoId) {
          this.ui.showNoVideoLayoutModal = true;
          this.newLayoutType = layoutType;
          return;
        }

        if (layoutType === 'text-only') {
          this.removeAllMediaFromQuestion();
          this.setScreenLayoutType(layoutType);
          return;
        }

        if (videoId || image) {
          this.setScreenLayoutType(layoutType);
          return;
        }
      }

      this.changeScreenLayout(layoutType);
    },

    changeScreenLayout(type) {
      this.setScreenLayoutType(type);
      this.ui.showAddLayoutMediaModal = true;
      this.forceCloseQuestionBuilder();
      this.ui.showLayoutMenu = false;
    },

    handleReplaceQuestion() {
      this.$emit('screen-actions-menu:replace-question', this.question);
    },

    handleRemoveScreen() {
      if (this.question && (this.question.has_flows || this.question.has_scores || this.question.has_files)) {
        this.ui.showDeleteScreenModal = true;
      } else {
        this.removeDynamicFormScreen(this.screen.number);
      }
    },

    removeAllMediaFromQuestion() {
      this.updateSelectedQuestion({
        ...this.question,
        layout: {
          type: 'text-only', image: null, video: null, videoSource: null,
        },
      });
      this.forceCloseQuestionBuilder();
    },

    forceCloseQuestionBuilder() {
      this.resetSelectedQuestion();
    },

    handleChangeMedia(type) {
      this.mediaType = type;
      this.ui.showAddLayoutMediaModal = true;
      this.handleCloseMediaMenu();
    },

    handleRemoveMedia() {
      const question = { ...this.question };

      // Reset question media properties and set question to text-only as no media is present
      question.layout = {
        type: 'text-only',
        image: null,
        videoSource: null,
      };

      this.updateSelectedQuestion(question);
      this.setScreenLayoutType('text-only');
      this.forceCloseQuestionBuilder();
    },

    handleToggleScreenConfigurationMenu(menuType, shouldOpen) {
      if (menuType === 'layout') {
        this.ui.showLayoutMenu = shouldOpen;
        this.handleCloseMediaMenu();
      } else if (menuType === 'media') {
        this.ui.showMediaMenu = shouldOpen;
        this.handleCloseScreenLayoutMenu();
      }
    },

    handleCloseScreenLayoutMenu() {
      // Note: @click.stop is very important to stop propagation to v-click-outside
      this.ui.showLayoutMenu = false;
    },

    handleCloseMediaMenu() {
      // Note: @click.stop is very important to stop propagation to v-click-outside
      this.ui.showMediaMenu = false;
    },

    handleSetAlignment(alignment) {
      this.updateSelectedQuestion({ ...this.question, text_align: alignment });
      this.forceCloseQuestionBuilder();
    },
  },
};
</script>

<style scoped lang="scss">
.ScreenActions-top {
  top: 0;
  position: absolute;
  margin: 16px 16px 0 0;
  max-width: 700px;
  height: 80px;
  right: 0;
  z-index: 2;

.AlignmentMenu {
  background: white;
  border: 1px solid lightgray;
  z-index: 10;

li {
  padding: 10px 0 10px 10px;

&:hover {
   cursor: pointer;
   background: #046ff0;
   color: white;
 }
}
}

.Button {
  background: white;
}
  .ReplaceQuestionButton {
    width: 36px;
    height: 28px;
    padding: 0;
    min-width: auto;
  }

.RemoveScreenButton {
  width: 36px;
  height: 28px;
  padding: 0;
  min-width: auto;
  margin-left: 16px;
}

.SelectLayoutButton, .SelectAlignmentButton {
  min-width: 85px;
  height: 28px;
  padding: 0;
}

  .SelectLayoutButton, .SelectAlignmentButton, .RemoveScreenButton {
    .Icon-s {
      font-size: 0.9rem;
      margin: 0;
    }
  }

.SelectMediaConfigurationButton {
  cursor: pointer;
  height: 28px;
  padding: 0;
  min-width: unset;
  width: 28px;
  margin-left: -5px;
  font-size: 0.9rem;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0
}
}
</style>
